import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormFieldCategory } from 'src/app/enum/form-field-category';
import { CheckListField } from 'src/app/model/checklist-field';
import { Submission } from 'src/app/model/paper';
import { SubmissionCoverage } from 'src/app/service/publication.service';
import { SubmissionsService } from 'src/app/service/submissions.service';

/**
 * Seção "Status de publicação" da página da submissão — rótulo, chip de
 * prontidão, lista de controles e o tri-estado de apresentação (no-show).
 *
 * Extraída do `paper-info` para virar FONTE ÚNICA: os Controles de publicação
 * pré-visualizam com este mesmo componente (em `previewOnly`), então o que o
 * chair configura e o que o autor vê não podem divergir por esquecimento.
 *
 * PERMISSÕES: todas vêm do `CheckListField` do controle — inclusive as do
 * no-show, que antes moravam em três booleanos do Event
 * (`noShowSessionChairCanEdit`/`CanView`/`noShowAuthorCanView`). Aqueles campos
 * nunca chegaram a ser enviados pelo backend e foram abandonados: uma regra só
 * para os seis controles, configurável na tabela unificada.
 */
@Component({
  selector: 'app-submission-publication-section',
  templateUrl: './submission-publication-section.component.html',
  styleUrls: ['./submission-publication-section.component.scss']
})
export class SubmissionPublicationSectionComponent {

  @Output() IEEECopyright = new EventEmitter();

  /** Repassado do preview: algo mudou e as outras visões precisam saber. */
  @Output() previewChange = new EventEmitter<void>();

  /** Todos os controles do evento, no-show inclusive. */
  @Input() covers: SubmissionCoverage[] = [];
  @Input() submission: Submission;
  @Input() showIeeeCopyright = false;
  @Input() isAdminOrChair = false;
  @Input() isSessionChair = false;
  @Input() isAuthor = false;
  /** Chair pré-visualizando outro perfil: nada é editável. */
  @Input() readOnly = false;
  /** Pré-visualização sem submissão real: as regras valem, a gravação não. */
  @Input() previewOnly = false;

  constructor(private submissionsService: SubmissionsService) { }

  /**
   * Os itens da LISTA de marcação. O no-show sai daqui: ele é um tri-estado
   * (não definido / apresentado / no-show) e se mostra em radios, não numa
   * caixa de marcação.
   */
  get coverItems(): SubmissionCoverage[] {
    return (this.covers || []).filter(cover => cover.field.category !== FormFieldCategory.NO_SHOW);
  }

  /** O controle de no-show, se o recurso estiver ligado neste evento. */
  private get noShowField(): CheckListField {
    const cover = (this.covers || []).find(item => item.field.category === FormFieldCategory.NO_SHOW);
    return cover ? cover.field : null;
  }

  get canEditNoShow(): boolean {
    const field = this.noShowField;

    if (!field || this.readOnly) {
      return false;
    }

    // Mesma forma do `canEdit` da lista de controles: o chair sempre edita
    // (exceto quando está olhando como autor); os demais, conforme o campo.
    return (this.isAdminOrChair && !this.isAuthor)
      || (this.isSessionChair && field.modifiableBy.sessionChair)
      || (this.isAuthor && field.modifiableBy.author);
  }

  get canViewNoShow(): boolean {
    const field = this.noShowField;

    if (!field) {
      return false;
    }

    return this.canEditNoShow
      || (this.isAdminOrChair && !this.isAuthor)
      || (this.isSessionChair && field.visibleTo.sessionChair)
      || (this.isAuthor && field.visibleTo.author);
  }

  /**
   * Grava o tri-estado. Otimista: reflete já na UI (silêncio = sucesso) e
   * reverte em erro.
   */
  setNoShow(value: boolean | null): void {
    if (!this.canEditNoShow || !this.submission || this.submission.noShow === value) {
      return;
    }

    // Preview: muda só o modelo local, pra o chip de prontidão reagir. Nada vai
    // ao servidor — a submissão é de fachada.
    if (this.previewOnly) {
      this.submission.noShow = value;
      this.previewChange.emit();
      return;
    }

    const previous = this.submission.noShow;
    this.submission.noShow = value;

    this.submissionsService.editSubmission({ noShow: value }, this.submission.id).subscribe(
      () => { /* ok */ },
      () => { this.submission.noShow = previous; }
    );
  }

  /**
   * Pronta = todos os controles marcados E, com o no-show ligado, apresentação
   * confirmada ("apresentado"). "Não definido" e "no-show" impedem.
   */
  get publicationIsReady(): boolean {
    const itemsReady = this.coverItems.every(cover => cover.answer);

    if (this.noShowField) {
      return itemsReady && this.submission && this.submission.noShow === false;
    }

    return itemsReady;
  }
}
